'use client';

import { showMessages } from '@intercom/messenger-js-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

const INTERCOM_SHOW_TIMEOUT_MS = 1000;

export const useIntercomMessenger = () => {
  const [isFallbackOpen, setIsFallbackOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isOpenRef = useRef(false);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const showIntercomMessages = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
      setIsFallbackOpen(true);
      return;
    }

    // Already open — no need for fallback detection
    if (isOpenRef.current) {
      showMessages();
      return;
    }

    let didShow = false;
    clearTimeout(timeoutRef.current);

    window.Intercom('onShow', () => {
      didShow = true;
      isOpenRef.current = true;
      clearTimeout(timeoutRef.current);
    });

    window.Intercom('onHide', () => {
      isOpenRef.current = false;
    });

    showMessages();

    // If the messenger didn't open, show fallback
    timeoutRef.current = setTimeout(() => {
      if (!didShow) {
        setIsFallbackOpen(true);
      }
    }, INTERCOM_SHOW_TIMEOUT_MS);
  }, []);

  return { showIntercomMessages, isFallbackOpen, setIsFallbackOpen };
};
